import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, idr, formatApiErrorDetail, openDocument } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { QuotationEditDialog } from "@/components/QuotationEditDialog";
import { RevisionHistory } from "@/components/RevisionHistory";
import {
  Send, Bot, Pencil, Trash2, CheckCircle2, XCircle, ReceiptText, MessageSquare, ArrowLeft, Sparkles, User, Building2, Mail, FileDown, Inbox,
} from "lucide-react";

export default function QuotationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [msg, setMsg] = useState("");
  const [offer, setOffer] = useState("");
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [approvalNote, setApprovalNote] = useState("");
  const [editing, setEditing] = useState(false);
  const remove = async () => {
    if (!window.confirm(`Hapus penawaran ${q.quote_number}? Tindakan ini tidak bisa dibatalkan.`)) return;
    try { await api.delete(`/quotations/${id}`); toast.success("Penawaran dihapus"); navigate("/quotations"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal menghapus"); }
  };

  const load = () => api.get(`/quotations/${id}`).then((r) => setQ(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!q) return <div className="p-8 text-muted-foreground font-mono text-sm">Memuat…</div>;

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const send = () => act(() => api.post(`/quotations/${id}/send`, { approval_note: approvalNote }), "Penawaran disetujui & dikirim (email)");
  const sendTG = () => act(async () => {
    const { data } = await api.post(`/quotations/${id}/telegram`);
    if (data.target === "admin") toast.info("Customer belum pernah chat via Telegram — PDF dikirim ke Telegram admin untuk diteruskan");
  }, "Penawaran dikirim via Telegram");
  const sendEmail = () => {
    const email = window.prompt("Kirim penawaran ke email:", q.customer_email || "");
    if (!email) return;
    act(() => api.post(`/quotations/${id}/email`, { email }), `Penawaran dikirim ke ${email}`);
  };
  const addNego = () => {
    if (!msg.trim()) { toast.error("Isi pesan negosiasi"); return; }
    act(async () => {
      await api.post(`/quotations/${id}/negotiate`, {
        sender: "customer", message: msg, offered_total: offer ? Number(offer) : null,
      });
      setMsg(""); setOffer("");
    }, "Penawaran nego customer dicatat");
  };
  const askAI = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/negotiate", {
        quotation_id: id, customer_message: msg || "Customer meminta harga terbaik.",
      });
      setAiResult(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAiLoading(false); }
  };
  const decide = (decision) =>
    act(() => api.post(`/quotations/${id}/decision`, { decision, note: "" }),
      decision === "accepted" ? "Penawaran DISETUJUI" : "Penawaran DITOLAK");
  const issueInvoice = () =>
    act(async () => { const { data } = await api.post(`/quotations/${id}/invoice`); navigate(`/invoices`); return data; },
      "Invoice diterbitkan & softcopy dikirim (mock email)");

  const REC = { accept: "Terima", counter: "Tawar Balik", reject: "Tolak" };

  return (
    <div data-testid="quotation-detail-page">
      <PageHeader overline={q.quote_number} title={q.customer_name}>
        <StatusBadge status={q.status} />
        {q.status !== "request" && <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="quotation-edit-btn"><Pencil className="w-4 h-4 mr-1" /> Edit</Button>}
        <Button variant="outline" size="sm" onClick={remove} data-testid="quotation-delete-btn" className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4 mr-1" /> Hapus</Button>
      </PageHeader>
      {editing && <QuotationEditDialog q={q} onClose={() => setEditing(false)} onSaved={(d) => { setQ(d); setEditing(false); }} />}

      <div className="px-8 py-4">
        <Link to="/quotations" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Kembali ke daftar
        </Link>
      </div>

      <div className="px-8 pb-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {q.status === "request" && (
            <div className="border border-sky-500/40 bg-sky-500/5 rounded-sm p-6" data-testid="request-note">
              <div className="flex items-center gap-2 overline text-sky-400 mb-2"><Inbox className="w-4 h-4" /> Permintaan Harga dari Website</div>
              <p className="text-sm whitespace-pre-wrap">{q.notes}</p>
              <Button onClick={() => navigate(`/ai?request=${id}`)} className="mt-4 rounded-sm bg-accent text-accent-foreground hover:opacity-90"><Bot className="w-4 h-4 mr-2" /> Proses dengan AI</Button>
            </div>
          )}
          <div className="border border-border bg-card rounded-sm">
            <div className="px-6 py-4 border-b border-border flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
              <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" /> {q.customer_name}</span>
              {q.customer_company && <span className="inline-flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {q.customer_company}</span>}
              {q.customer_email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {q.customer_email}</span>}
            </div>
            <table className="w-full text-sm">
              <thead className="bg-secondary">
                <tr className="text-left overline text-muted-foreground">
                  <th className="px-4 py-2">Item</th><th className="px-4 py-2">Qty</th>
                  <th className="px-4 py-2">Harga Jual</th><th className="px-4 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {q.items.map((it, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3"><div className="font-medium">{it.product_name}</div><div className="text-xs text-muted-foreground font-mono">{it.series}</div></td>
                    <td className="px-4 py-3">{it.quantity}</td>
                    <td className="px-4 py-3 font-mono text-xs">{idr(it.unit_price)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-right">{idr(it.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="p-6 border-t border-border">
              <div className="ml-auto max-w-xs space-y-1 text-sm">
                <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span className="font-mono">{idr(q.subtotal)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>PPN</span><span className="font-mono">{idr(q.tax_amount)}</span></div>
                <div className="flex justify-between font-heading font-black text-lg pt-2 border-t border-border"><span>Total</span><span className="font-mono">{idr(q.total)}</span></div>
                <div className="text-[11px] text-muted-foreground pt-1">*Harga belum termasuk PPN</div>
              </div>
            </div>
          </div>

          <div className="border border-border bg-card rounded-sm">
            <div className="px-6 py-4 border-b border-border overline text-muted-foreground flex items-center gap-2">
              <MessageSquare className="w-4 h-4" /> Riwayat Negosiasi
            </div>
            <div className="p-6 space-y-3 max-h-80 overflow-y-auto">
              {(!q.negotiation_rounds || q.negotiation_rounds.length === 0) && (
                <p className="text-sm text-muted-foreground">Belum ada negosiasi.</p>
              )}
              {q.negotiation_rounds?.map((r) => (
                <div key={r.id} className={`p-3 rounded-sm border text-sm ${r.sender === "customer" ? "border-accent/30 bg-accent/5" : "border-primary/30 bg-primary/5"}`}>
                  <div className="overline text-muted-foreground mb-1">{r.sender === "customer" ? "Customer" : "Admin"}</div>
                  <p>{r.message}</p>
                  {r.offered_total != null && <div className="font-mono text-xs mt-1 text-accent">Tawaran: {idr(r.offered_total)}</div>}
                </div>
              ))}
            </div>
          </div>

          <RevisionHistory history={q.revision_history || []} />
        </div>

        <div className="space-y-6">
          <div className="border border-border bg-card rounded-sm p-6">
            <div className="overline text-muted-foreground mb-3">Aksi Penawaran</div>
            <div className="space-y-2">
              {q.status === "request" && (
                <Button data-testid="fill-ai-btn" onClick={() => navigate(`/ai?request=${id}`)} className="w-full rounded-sm bg-accent text-accent-foreground hover:opacity-90"><Bot className="w-4 h-4 mr-2" /> Isi Harga via AI</Button>
              )}
              {q.status !== "request" && (
                <Button data-testid="download-pdf-btn" onClick={() => openDocument(`/quotations/${id}/pdf`).catch(() => toast.error("Gagal membuka PDF"))} variant="outline" className="w-full rounded-sm"><FileDown className="w-4 h-4 mr-2" /> Lihat / Unduh PDF</Button>
              )}
              {q.status === "draft" && (
                <>
                  <div className="text-xs text-muted-foreground mb-1" data-testid="approval-hint">Menunggu persetujuan Anda sebelum dikirim ke customer.</div>
                  <Textarea data-testid="approval-note" value={approvalNote} onChange={(e) => setApprovalNote(e.target.value)} placeholder="Catatan persetujuan (opsional): alasan/keterangan…" className="rounded-sm bg-secondary text-sm min-h-[60px] mb-1" />
                  <Button data-testid="send-quote-btn" onClick={send} disabled={busy} className="w-full rounded-sm"><Send className="w-4 h-4 mr-2" /> Setujui & Kirim ke Customer</Button>
                </>
              )}
              {q.status !== "request" && (
                <Button data-testid="send-telegram-btn" onClick={sendTG} disabled={busy} variant="outline" className="w-full rounded-sm border-sky-600 text-sky-700 hover:bg-sky-50"><Send className="w-4 h-4 mr-2" /> Kirim via Telegram</Button>
              )}
              {q.status !== "request" && (
                <Button data-testid="send-email-btn" onClick={sendEmail} disabled={busy} variant="outline" className="w-full rounded-sm"><Mail className="w-4 h-4 mr-2" /> Kirim via Email</Button>
              )}
              {q.approved_by && (
                <div className="text-[11px] text-muted-foreground pt-1" data-testid="approval-info">Disetujui oleh {q.approved_by}{q.approval_note ? ` · "${q.approval_note}"` : ""}</div>
              )}
              {["sent", "negotiating"].includes(q.status) && (
                <>
                  <Button data-testid="accept-btn" onClick={() => decide("accepted")} disabled={busy} className="w-full rounded-sm bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="w-4 h-4 mr-2" /> Setujui (Final)</Button>
                  <Button data-testid="reject-btn" onClick={() => decide("rejected")} disabled={busy} variant="outline" className="w-full rounded-sm border-destructive text-destructive hover:bg-destructive/10"><XCircle className="w-4 h-4 mr-2" /> Tolak</Button>
                </>
              )}
              {q.status === "accepted" && (
                <Button data-testid="issue-invoice-btn" onClick={issueInvoice} disabled={busy} className="w-full rounded-sm bg-accent text-accent-foreground hover:opacity-90"><ReceiptText className="w-4 h-4 mr-2" /> Terbitkan Invoice</Button>
              )}
              {q.status === "invoiced" && (
                <Link to="/invoices" className="block text-center text-sm text-primary hover:underline py-2">Lihat Invoice →</Link>
              )}
            </div>
          </div>

          <div className="border border-border bg-card rounded-sm p-6">
            <div className="overline text-muted-foreground mb-3">Negosiasi Customer</div>
            <Textarea data-testid="nego-message" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Pesan / permintaan customer…" className="rounded-sm bg-secondary min-h-[80px] text-sm" />
            <Label className="overline text-muted-foreground mt-3 block">Tawaran Total (opsional)</Label>
            <Input data-testid="nego-offer" type="number" value={offer} onChange={(e) => setOffer(e.target.value)} className="mt-1 rounded-sm bg-secondary" />
            <Button data-testid="add-nego-btn" onClick={addNego} disabled={busy} variant="outline" className="w-full mt-3 rounded-sm"><MessageSquare className="w-4 h-4 mr-2" /> Catat Nego</Button>
            <Button data-testid="ai-negotiate-btn" onClick={askAI} disabled={aiLoading} className="w-full mt-2 rounded-sm bg-accent text-accent-foreground hover:opacity-90"><Bot className="w-4 h-4 mr-2" /> {aiLoading ? "AI berpikir…" : "Minta Saran AI"}</Button>

            {aiResult && (
              <div className="mt-4 p-4 border border-accent/30 bg-accent/5 rounded-sm text-sm space-y-2" data-testid="ai-nego-result">
                <div className="flex items-center gap-2 overline text-accent"><Sparkles className="w-3.5 h-3.5" /> Rekomendasi AI: {REC[aiResult.recommendation] || aiResult.recommendation}</div>
                <p className="text-foreground">{aiResult.reply}</p>
                {aiResult.suggested_total != null && <div className="font-mono text-xs">Usulan total: {idr(aiResult.suggested_total)}</div>}
                <div className="text-xs text-muted-foreground font-mono">Batas min. (margin 3%): {idr(aiResult.min_total)}</div>
                <p className="text-xs text-muted-foreground italic">{aiResult.reasoning}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
